// The 14-day right of withdrawal for plans, upgrades and extensions, and who
// it is shown to (approval 390: «only in EU», read as the EU/EEA, whose
// consumer law gives the same 14 days). Pure: the footer, the checkout note,
// the purchase e-mail, the activation page and the date-saving server actions
// all read these rules, so the screen and the server can't disagree.
// Failure modes: tests/withdrawal/failure-modes.md.

import { EU_COUNTRIES } from "@/lib/english-market";

/** Days the buyer can withdraw, counted in Copenhagen calendar days from the purchase day. */
export const WITHDRAWAL_DAYS = 14;

/** The EU plus the EEA countries outside it (Norway, Iceland, Liechtenstein). */
export const EEA_COUNTRIES: ReadonlySet<string> = new Set(
  Array.from(EU_COUNTRIES).concat(["NO", "IS", "LI"]),
);

/**
 * Page locales whose market is in the EEA. English and Faroese are not: their
 * visitors can be anywhere, so the IP country decides for them.
 */
export const EEA_LOCALES: ReadonlySet<string> = new Set([
  "da",
  "sv",
  "no",
  "de",
  "fr",
  "it",
  "es",
  "pt",
  "el",
  "nl",
  "fi",
  "is",
]);

/** Checkout currencies charged only to EEA markets (see englishPagePricing and the proxy's LOCALE_CURRENCIES). */
export const EEA_CURRENCIES: ReadonlySet<string> = new Set([
  "eur",
  "dkk",
  "sek",
  "nok",
  "isk",
]);

/** Where the withdrawal page lives, per locale (next-intl pathnames for "/fortryd-aftale"). */
export const WITHDRAWAL_PATHNAMES = {
  en: "/withdraw-from-contract",
  da: "/fortryd-aftale",
  sv: "/angra-avtalet",
  no: "/angre-avtalen",
  de: "/vertrag-widerrufen",
} as const;

/** The withdrawal page's absolute URL for a locale (others use the English path under their own locale). */
export function withdrawalPageUrl(locale: string): string {
  const path =
    WITHDRAWAL_PATHNAMES[locale as keyof typeof WITHDRAWAL_PATHNAMES] ??
    WITHDRAWAL_PATHNAMES.en;
  return `https://guest.gallery/${locale}${path}`;
}

/** Whether a visitor or buyer belongs to an EEA market: any EEA page locale, or an EEA IP country. */
export function isEeaBuyer(input: {
  locales: ReadonlyArray<string | null | undefined>;
  country?: string | null;
}): boolean {
  if (input.locales.some((locale) => !!locale && EEA_LOCALES.has(locale))) {
    return true;
  }
  const country = (input.country ?? "").toUpperCase();
  return EEA_COUNTRIES.has(country);
}

/** Whether a checkout currency (gg-currency cookie / Stripe session) is an EEA market's. */
export function isEeaCurrency(currency: string | null | undefined): boolean {
  return !!currency && EEA_CURRENCIES.has(currency.toLowerCase());
}

/** A moment's calendar day in Copenhagen, as YYYY-MM-DD. */
export function copenhagenDay(moment: Date | string | number): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Copenhagen",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(moment));
}

/** Whole calendar days from one YYYY-MM-DD to another (negative when `to` is earlier). */
export function daysBetween(from: string, to: string): number {
  return Math.round(
    (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) /
      86_400_000,
  );
}

export interface EarlyStartInput {
  /** The purchase time: the event row's created_at (the webhook creates it on payment). */
  purchasedAt: Date | string | null | undefined;
  /** The event date being saved. */
  eventDate: Date | string;
  /** Trials are free: no right to withdraw from. */
  isTrial: boolean | null | undefined;
  /** Bought through Stripe (events.stripe_webhook_event_id); admin-made events weren't sold. */
  isPaid: boolean;
  /** isEeaBuyer for the event's purchase locale, the page locale and the IP country. */
  isEea: boolean;
  now?: number;
}

/**
 * Whether saving this event date needs the buyer's request to start at once
 * (forbrugeraftaleloven § 25): the date is on or before day 14 after the
 * purchase day, and the buyer's 14 days are still running. The 14 days before
 * the event (uploads open) don't count as an early start (founder, 5 Nov).
 */
export function needsEarlyStartConsent(input: EarlyStartInput): boolean {
  if (!input.isPaid || input.isTrial || !input.isEea || !input.purchasedAt) {
    return false;
  }
  const purchased = new Date(input.purchasedAt);
  const eventDate = new Date(input.eventDate);
  if (!Number.isFinite(purchased.getTime()) || !Number.isFinite(eventDate.getTime())) {
    return false;
  }
  const purchaseDay = copenhagenDay(purchased);
  const today = copenhagenDay(input.now ?? Date.now());
  if (daysBetween(purchaseDay, today) > WITHDRAWAL_DAYS) return false;
  return daysBetween(purchaseDay, copenhagenDay(eventDate)) <= WITHDRAWAL_DAYS;
}

/** The server actions' refusal when the box was needed but not ticked. */
export const EARLY_START_CONSENT_REQUIRED = "early_start_consent_required";

/** Whether the buyer's 14 days are still running (through day 14 after the purchase day, Copenhagen time). */
export function isWithinWithdrawalPeriod(
  purchasedAt: Date | string | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!purchasedAt) return false;
  const purchased = new Date(purchasedAt);
  if (!Number.isFinite(purchased.getTime())) return false;
  return daysBetween(copenhagenDay(purchased), copenhagenDay(now)) <= WITHDRAWAL_DAYS;
}

/** What the dashboard knows about a purchase, for the early-start box and the withdrawal link. */
export interface PurchaseWithdrawalFacts {
  purchasedAt: string | null;
  isPaid: boolean;
  isTrial: boolean;
  isEea: boolean;
}
